import { useState, type ReactNode } from "react";
import { APIError } from "../../api";
import type { T } from "../../i18n";
import { Codes } from "./Codes";
import { Venues } from "./Venues";
import { Staff } from "./Staff";

type Tab = "codes" | "venues" | "staff";

/** What every backoffice screen gets from the shell. */
export interface BoProps {
  t: T;
  locale: string;
  /** Any request can meet an expired token; every screen hands it back here. */
  onError: (e: unknown) => string;
}

/**
 * The Mana team's own screens: invitation codes, the venues they open, and
 * the people who signed up with them. Everything `deploy.sh prod invite` does,
 * from a browser, under the same rules — the server applies them in one place.
 */
export function Backoffice({
  t,
  locale,
  localeToggle,
  onLogout,
}: {
  t: T;
  locale: string;
  localeToggle: ReactNode;
  onLogout: () => void;
}) {
  const [tab, setTab] = useState<Tab>("codes");
  // "Who used this code?" — set from the codes table, read by the staff list.
  const [codeFilter, setCodeFilter] = useState<string | null>(null);
  // "Issue a code for this venue" — set from the venues table, read once by
  // the issue form when the codes tab opens.
  const [issueFor, setIssueFor] = useState<string | null>(null);
  const tabs: Tab[] = ["codes", "venues", "staff"];

  // A 401 means the twelve-hour token ran out: back to the login screen rather
  // than a page of red errors. Anything else becomes a sentence to show.
  const onError = (e: unknown): string => {
    if (e instanceof APIError && e.status === 401) {
      onLogout();
      return "";
    }
    return errorText(t, e);
  };
  const props: BoProps = { t, locale, onError };

  return (
    <div className="shell">
      <header className="topbar">
        <strong>{t("bo.title")}</strong>
        <nav>
          {tabs.map((tb) => (
            <button key={tb} className={tab === tb ? "active" : ""} onClick={() => setTab(tb)}>
              {t(`bo.nav.${tb}`)}
            </button>
          ))}
        </nav>
        <div className="topbar-right">
          {localeToggle}
          <button className="logout" onClick={onLogout}>
            {t("nav.logout")}
          </button>
        </div>
      </header>
      <main>
        {tab === "codes" && (
          <Codes
            {...props}
            initialVenueId={issueFor}
            onShowStaff={(code) => {
              setCodeFilter(code);
              setTab("staff");
            }}
          />
        )}
        {tab === "venues" && (
          <Venues
            {...props}
            onIssueCode={(venueId) => {
              setIssueFor(venueId);
              setTab("codes");
            }}
          />
        )}
        {tab === "staff" && <Staff {...props} codeFilter={codeFilter} onClearCode={() => setCodeFilter(null)} />}
      </main>
    </div>
  );
}

/**
 * A refusal's reason in the reader's language, falling back to the server's
 * English sentence for a reason this build does not know yet.
 */
export function errorText(t: T, e: unknown): string {
  if (e instanceof APIError) {
    if (e.reason) {
      const key = `bo.err.${e.reason}`;
      const text = t(key);
      if (text !== key) return text;
    }
    if (e.status < 500 && e.message) return e.message;
  }
  return t("common.error");
}

/** Fill `{name}` placeholders; the catalogs keep whole sentences. */
export function fill(text: string, vars: Record<string, string | number>): string {
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

export function venueLabel(v: { name: string; neighborhood: string }): string {
  return v.neighborhood ? `${v.name} · ${v.neighborhood}` : v.name;
}

export function shortDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
}
